// Holiday context provider

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';

import {
  DEFAULT_CALENDAR_ID,
  DEFAULT_CALENDAR_NAME,
  DEFAULT_HOLIDAYS_ENDPOINT,
} from './constants';
import { getCalendarColor } from './colors';
import {
  parseICalFile,
  parseICalString,
  extractCalendarName,
  toHolidayEvents,
} from './parsing';
import {
  loadHolidayData,
  addCalendarToStorage,
  removeCalendarFromStorage,
  toggleCalendarInStorage,
  renameCalendarInStorage,
  addEventsToStorage,
  getEventsForDate,
} from './storage';
import type { Calendar, Holiday } from './types';
import { useCurrentUser } from '@/features/users/users.hooks';

interface HolidayContextValue {
  calendars: Calendar[];
  getHolidaysForDate: (date: Date) => Holiday[];
  addCalendar: (file: File) => Promise<void>;
  removeCalendar: (id: string) => void;
  toggleCalendar: (id: string, enabled: boolean) => void;
  renameCalendar: (id: string, newName: string) => void;
  isLoading: boolean;
  error: Error | null;
}

const HolidayContext = createContext<HolidayContextValue | null>(null);

export function useHolidays(): HolidayContextValue {
  const context = useContext(HolidayContext);
  if (!context) {
    throw new Error('useHolidays must be used within HolidayProvider');
  }
  return context;
}

export function HolidayProvider({ children }: { children: React.ReactNode }) {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const user = useCurrentUser();

  // Initialize calendars and fetch defaults when user is logged in
  useEffect(() => {
    const initializeHolidays = async () => {
      // Don't do anything if user is not signed in
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Load existing calendars from storage
        const stored = loadHolidayData();

        // Check if default calendar already exists
        const hasDefault = stored?.calendars.some(
          (c) => c.id === DEFAULT_CALENDAR_ID,
        );

        if (hasDefault) {
          // Just load from storage, no need to fetch
          setCalendars(stored!.calendars);
          setIsLoading(false);
          return;
        }

        // No default calendar yet - fetch Dutch holidays
        const response = await fetch(DEFAULT_HOLIDAYS_ENDPOINT);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch default holidays: ${response.status} ${response.statusText}`,
          );
        }

        const icalData = await response.text();

        // Parse with error handling
        let events;
        try {
          events = parseICalString(icalData);
        } catch (parseError) {
          throw new Error(
            `Failed to parse default holidays: ${parseError instanceof Error ? parseError.message : 'Invalid iCal format'}`,
          );
        }

        // Create default calendar
        const defaultCalendar: Calendar = {
          id: DEFAULT_CALENDAR_ID,
          name: DEFAULT_CALENDAR_NAME,
          color: getCalendarColor(0), // First color
          source: 'default',
          enabled: true,
          lastModified: new Date().toISOString(),
        };

        // Save to storage with error handling
        try {
          addCalendarToStorage(defaultCalendar);
          addEventsToStorage(toHolidayEvents(events, DEFAULT_CALENDAR_ID));
        } catch (storageError) {
          throw new Error(
            `Failed to save default holidays: ${storageError instanceof Error ? storageError.message : 'Storage error'}`,
          );
        }

        // Update state with existing calendars + new default
        const existingCalendars = stored?.calendars || [];
        setCalendars([defaultCalendar, ...existingCalendars]);
      } catch (err) {
        console.error('Failed to fetch default holidays:', err);
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to load default holidays');
        setError(error);

        // Load any existing calendars even if default fetch failed
        const stored = loadHolidayData();
        if (stored) {
          setCalendars(stored.calendars);
        }

        // Show user-friendly toast (non-blocking - app still works without default holidays)
        toast.error('Could not load default holiday calendar', {
          description: 'You can still upload your own iCal files in Settings.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeHolidays();
  }, [user]);

  /**
   * Get holidays for a specific date
   */
  const getHolidaysForDate = useCallback(
    (date: Date): Holiday[] => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const events = getEventsForDate(dateStr);

      // Filter to only enabled calendars
      const enabledCalendarIds = new Set(
        calendars.filter((c) => c.enabled).map((c) => c.id),
      );

      return events
        .filter((event) => enabledCalendarIds.has(event.calendarId))
        .map((event) => {
          const calendar = calendars.find((c) => c.id === event.calendarId);
          if (!calendar) return null;

          return {
            id: `${event.calendarId}-${event.date}-${event.title}`,
            calendar,
            event,
          };
        })
        .filter((h): h is Holiday => h !== null);
    },
    [calendars],
  );

  /**
   * Add calendar from uploaded file
   */
  const addCalendar = useCallback(
    async (file: File) => {
      try {
        // Parse iCal file
        const events = await parseICalFile(file);

        // Extract calendar name from file or use filename
        const icalText = await file.text();
        const extractedName = extractCalendarName(icalText);
        const calendarName = extractedName || file.name.replace(/\.ics$/, '');

        // Generate calendar ID
        const calendarId = crypto.randomUUID();

        // Assign color based on current calendar count
        const calendarIndex = calendars.length;
        const color = getCalendarColor(calendarIndex);

        // Create calendar
        const newCalendar: Calendar = {
          id: calendarId,
          name: calendarName,
          color,
          source: 'user',
          enabled: true,
          lastModified: new Date().toISOString(),
        };

        // Save to storage
        addCalendarToStorage(newCalendar);
        addEventsToStorage(toHolidayEvents(events, calendarId));

        // Update state
        setCalendars((prev) => [...prev, newCalendar]);

        toast.success(`Added calendar: ${calendarName}`);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to parse iCal file';
        toast.error(message);
        throw err;
      }
    },
    [calendars.length],
  );

  /**
   * Remove calendar
   */
  const removeCalendar = useCallback((id: string) => {
    // Don't allow removing default calendar
    if (id === DEFAULT_CALENDAR_ID) {
      toast.error('Cannot remove default calendar');
      return;
    }

    removeCalendarFromStorage(id);
    setCalendars((prev) => prev.filter((c) => c.id !== id));
    toast.success('Calendar removed');
  }, []);

  /**
   * Toggle calendar enabled state
   */
  const toggleCalendar = useCallback((id: string, enabled: boolean) => {
    toggleCalendarInStorage(id, enabled);
    setCalendars((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled } : c)),
    );
  }, []);

  /**
   * Rename calendar
   */
  const renameCalendar = useCallback((id: string, newName: string) => {
    if (!newName.trim()) {
      toast.error('Calendar name cannot be empty');
      return;
    }

    renameCalendarInStorage(id, newName);
    setCalendars((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, name: newName, lastModified: new Date().toISOString() }
          : c,
      ),
    );
    toast.success('Calendar renamed');
  }, []);

  const contextValue = useMemo(
    () => ({
      calendars,
      getHolidaysForDate,
      addCalendar,
      removeCalendar,
      toggleCalendar,
      renameCalendar,
      isLoading,
      error,
    }),
    [
      calendars,
      getHolidaysForDate,
      addCalendar,
      removeCalendar,
      toggleCalendar,
      renameCalendar,
      isLoading,
      error,
    ],
  );

  return (
    <HolidayContext.Provider value={contextValue}>
      {children}
    </HolidayContext.Provider>
  );
}
